<template>
    <PersonCardWrapper
        :class="{'deleted': deleted}"
        :edit-mode="editMode"
        :delay-render="!noEditBodyRendered && delayRender">
        <NestedForm
            ref="personValidator"
            v-slot="{meta: {valid}, errors, validate}">
            <div class="d-flex justify-content-between align-items-center flex-wrap name-edit-header">
                <div class="name-heading">
                    <h2 class="m-0">
                        <PersonName
                            :index="index"
                            :person="person"
                            :registering-person-type="registeringPersonType" />
                    </h2>
                </div>
                <div
                    v-if="!editMode && !viewOnly"
                    class="d-flex">
                    <base-button
                        v-if="selectMode"
                        size="sm"
                        @click="$emit('selectedPerson', personKey)">
                        {{ $t('eventRegistration.participant.select') }}
                    </base-button>
                    <template v-else-if="!deleted">
                        <BaseTooltip
                            v-if="registeringPersonType === 'invoiceRecipient' && !hideDelete"
                            :content="deleteLabel">
                            <base-button
                                size="sm"
                                type="danger"
                                :aria-label="deleteLabel"
                                @click="deletePerson">
                                <font-awesome-icon
                                    icon="times"
                                    fixed-width />
                            </base-button>
                        </BaseTooltip>
                        <BaseTooltip :content="$t('eventRegistration.participant.edit')">
                            <base-button
                                size="sm"
                                :aria-label="$t('eventRegistration.participant.edit')"
                                @click="activateEditMode">
                                <font-awesome-icon
                                    icon="pen"
                                    fixed-width />
                            </base-button>
                        </BaseTooltip>
                    </template>
                    <base-button
                        v-else-if="canSeeAddParticipantButton"
                        size="sm"
                        type="success"
                        @click="restorePerson">
                        {{ $t('eventRegistration.participant.restore') }}
                    </base-button>
                </div>
            </div>
            <div v-show="!editMode">
                <div v-if="!deleted">
                    <div
                        class="mt-2"
                        style="white-space: nowrap;">
                        {{ person.streetValue }} {{ person.houseNumber }}
                    </div>
                    <div>
                        {{ person.zipValue }} {{ person.cityValue }}
                    </div>
                    <div v-if="person.birthday && (new Date(person.birthday)).getTime()">
                        {{ $df($moment(person.birthday), 'dateShort') }}
                    </div>
                    <div>
                        {{ person.email }} <span v-if="person.email && person.phone">/</span> {{ person.phone }}
                    </div>
                    <div
                        v-for="error in errors"
                        :key="error">
                        <div
                            class="invalid-feedback"
                            style="display: block">
                            {{ error }}
                        </div>
                    </div>
                </div>
                <div v-if="deleted">
                    <h3 class="text-danger mt-2">
                        <template v-if="changeWordingRegistrationBuy">
                            {{ $t('eventRegistration.participant.personDeleted') }}
                        </template>
                        <template v-else-if="isCaregiver">
                            {{ $t('eventRegistration.participant.caregiverDeleted') }}
                        </template>
                        <template v-else>
                            {{ $t('eventRegistration.participant.participantDeleted') }}
                        </template>
                    </h3>
                </div>
            </div>
            <!-- Benutze v-if, damit Validierung von gelöschten Personen deaktiviert wird. -->
            <div
                v-if="groupRegistrarShouldNotPreloadAllPersons && !(noEditBodyRendered)"
                v-show="editMode"
                class="mt-4">
                <base-input
                    v-if="getNumberOfRemainingPlacesWithCurrentRegistrations !== null"
                    :model-value="-getNumberOfRemainingPlacesWithCurrentRegistrations"
                    :rules="{tooManyParticipants: true}"
                    :name="personKey + '.participantPlacesCheck'"
                    no-margin>
                    <input class="d-none">
                </base-input>
                <div class="row">
                    <div class="col-md-6">
                        <OrfEval
                            :orf-type="registeringPersonType"
                            orf-name="contactType">
                            <template #default="{required}">
                                <CardSelect
                                    v-model="person.type"
                                    :rules="{one_of: invoiceRecipientTypeRestriction === null ? [1,2] : [invoiceRecipientTypeRestriction]}"
                                    :fixed-width="false"
                                    :name="$t('eventRegistration.person.contactType')"
                                    :required="required"
                                    type="radio">
                                    <CardSelectItem
                                        v-for="personType in personTypes?.filter(type => invoiceRecipientTypeRestriction === null || invoiceRecipientTypeRestriction === type.key)"
                                        :key="personType.key"
                                        :icon="personType.key === 1 ? 'user' : 'industry'"
                                        :value="personType.key">
                                        {{ $t('eventRegistration.person.' + personType.label) }}
                                    </CardSelectItem>
                                </CardSelect>
                            </template>
                        </OrfEval>

                        <template v-if="person.type !== 2">
                            <ParticipantSalutation
                                v-if="registeringPersonType === 'participant'"
                                :person-key="personKey"
                                :registering-person-type="registeringPersonType" />
                            <InvoiceRecipientSalutation
                                v-if="registeringPersonType === 'invoiceRecipient'"
                                :person-key="personKey"
                                :registering-person-type="registeringPersonType" />
                            <ParticipantGender
                                v-if="registeringPersonType === 'participant'"
                                :person-key="personKey"
                                :registering-person-type="registeringPersonType" />
                            <InvoiceRecipientGender
                                v-if="registeringPersonType === 'invoiceRecipient'"
                                :person-key="personKey"
                                :registering-person-type="registeringPersonType" />
                            <OrfEval
                                :orf-type="registeringPersonType"
                                orf-name="title">
                                <template #default="{required}">
                                    <BaseSelect
                                        v-model="person.acadTitle"
                                        :label="$t('eventRegistration.person.acadTitle')"
                                        :required="required"
                                        :placeholder="$t('eventRegistration.person.noTitle')"
                                        clearable>
                                        <BaseOption
                                            v-for="title in acadTitles"
                                            :key="title.id"
                                            :label="title.title"
                                            :value="title.id" />
                                    </BaseSelect>
                                    <BaseSelect
                                        v-model="person.title"
                                        :label="$t('eventRegistration.person.title')"
                                        :required="required"
                                        :placeholder="$t('eventRegistration.person.noTitle')"
                                        clearable>
                                        <BaseOption
                                            v-for="title in titles"
                                            :key="title.id"
                                            :label="title.title"
                                            :value="title.id" />
                                    </BaseSelect>
                                </template>
                            </OrfEval>
                            <OrfEval
                                :orf-type="registeringPersonType"
                                orf-name="name">
                                <template #default="{required}">
                                    <base-input
                                        v-model="person.firstname"
                                        :label="$t('eventRegistration.person.firstname')"
                                        :rules="{max: 50}"
                                        :required="required" />
                                </template>
                            </OrfEval>
                            <OrfEval
                                :orf-type="registeringPersonType"
                                orf-name="name">
                                <template #default="{required}">
                                    <base-input
                                        v-model="person.lastname"
                                        :label="$t('eventRegistration.person.lastname')"
                                        :rules="{max: 100}"
                                        :required="required" />
                                </template>
                            </OrfEval>
                            <OrfEval
                                :orf-type="registeringPersonType"
                                orf-name="birthday">
                                <template #default="{required}">
                                    <base-input
                                        v-slot="{field}"
                                        v-model="person.birthday"
                                        :label="$t('eventRegistration.person.birthday')"
                                        :required="required"
                                        :rules="birthdayRules"
                                        type="date">
                                        <div class="d-md-none w-100">
                                            <BaseDateSelectInput v-bind="field" />
                                        </div>
                                        <div class="d-none d-md-block w-100">
                                            <input
                                                type="date"
                                                class="form-control"
                                                v-bind="field">
                                        </div>
                                    </base-input>
                                    <small
                                        v-if="birthdayNotStandardized"
                                        class="text-warning">
                                        <font-awesome-icon icon="exclamation-circle" />
                                        {{ $t('eventRegistration.person.dateWarning', {date: recognizedBirthday}) }}
                                    </small>
                                </template>
                            </OrfEval>
                        </template>

                        <template v-else>
                            <OrfEval
                                :orf-type="registeringPersonType"
                                orf-name="name">
                                <template #default="{required}">
                                    <base-input
                                        v-model="person.lastname"
                                        :rules="{max: 100}"
                                        :label="$t('eventRegistration.person.organisationName')"
                                        :required="required" />
                                </template>
                            </OrfEval>
                            <base-input
                                v-model="person.toTheAttention"
                                :label="$t('eventRegistration.person.toTheAttention')"
                                :rules="{max: 100}" />
                        </template>
                    </div>
                    <div class="col-md-6">
                        <OrfEval
                            :orf-type="registeringPersonType"
                            orf-name="address">
                            <template #default="{required}">
                                <RegistrationAddressFormMain
                                    :is-organisation="person.type === 2"
                                    :person-key="personKey"
                                    :required="required"
                                    :only-persons-as-participants="onlyPersonsAsParticipants && personKey !== 'invoiceRecipient'" />
                            </template>
                        </OrfEval>
                        <OrfEval
                            :orf-type="registeringPersonType"
                            orf-name="phone">
                            <template #default="{required}">
                                <base-input
                                    v-model="person.phone"
                                    :label="$t('eventRegistration.person.phone')"
                                    :required="required"
                                    :rules="{phone: !!person?.phone?.length, pending: !!person.phone?.length && !!checkingPhone, phoneInvalid: !!person?.phone?.length && !!phoneInvalid, max: 70}" />
                            </template>
                        </OrfEval>
                        <OrfEval
                            :orf-type="registeringPersonType"
                            orf-name="mail">
                            <template #default="{required}">
                                <base-input
                                    v-model="person.email"
                                    :label="$t('eventRegistration.person.email')"
                                    :required="required"
                                    :rules="{email: true, max: 70, regex: /^.*@[^-].*[^-]\..*$/}">
                                    <!-- regex should prevent email addresses like test@-abc.de or test@abc-.de -->
                                </base-input>
                            </template>
                        </OrfEval>
                        <OrfEval
                            v-if="person.type === 2"
                            :orf-type="registeringPersonType"
                            orf-name="vatId">
                            <template #default="{required}">
                                <base-input
                                    v-model="person.vatId"
                                    :label="$t('eventRegistration.person.vatId')"
                                    :required="required"
                                    :rules="{max: 20}" />
                            </template>
                        </OrfEval>
                    </div>
                </div>
                <template v-if="registeringPersonType === 'participant'">
                    <div
                        v-if="eventData.queryListOfParticipantsAgreement"
                        class="mb-3">
                        <base-input
                            :model-value="person.listOfParticipantsAllowed"
                            :label="$t('eventRegistration.person.queryListOfParticipantsAgreement')">
                            <div class="input-group gap-4">
                                <BaseRadio
                                    v-model="person.listOfParticipantsAllowed"
                                    :name="1">
                                    Ja
                                </BaseRadio>
                                <BaseRadio
                                    v-model="person.listOfParticipantsAllowed"
                                    :name="0">
                                    Nein
                                </BaseRadio>
                            </div>
                        </base-input>
                        <div class="small photos-agreement-text custom-control">
                            {{ $t('eventRegistration.person.queryListOfParticipantsAgreementInfo') }}
                        </div>
                    </div>
                    <div
                        v-if="eventData.queryPhotosAgreement"
                        class="mb-3">
                        <base-input
                            :model-value="person.photosAllowed"
                            :label="$t('eventRegistration.person.photosAgreement')"
                            required>
                            <div class="input-group gap-4">
                                <BaseRadio
                                    v-model="person.photosAllowed"
                                    :name="1">
                                    Ja
                                </BaseRadio>
                                <BaseRadio
                                    v-model="person.photosAllowed"
                                    :name="0">
                                    Nein
                                </BaseRadio>
                            </div>
                        </base-input>
                        <div class="small photos-agreement-text custom-control">
                            {{ queryPhotosAgreementText }}
                        </div>
                        <a
                            v-if="photosAgreementTextShortened && shortQueryPhotosAgreementTextMustBeShortened"
                            class="custom-control small"
                            href="#"
                            @click.prevent="photosAgreementTextShortened = false">
                            {{ $t('eventRegistration.person.showFullText') }}
                        </a>
                    </div>
                </template>
                <template v-if="registeringPersonType === 'invoiceRecipient'">
                    <base-input
                        v-if="showOwnResourcesAgreement && chargeable"
                        v-model="person.ownResourcesAgreed"
                        :rules="{ required: { allowFalse: false } }"
                        :name="$t('eventRegistration.person.ownResourcesAgreementName')">
                        <BaseCheckbox
                            v-model="person.ownResourcesAgreed"
                            multi-line>
                            {{ $t('eventRegistration.person.ownResourcesAgreement') }}
                        </BaseCheckbox>
                    </base-input>
                </template>
                <div class="d-flex justify-content-between mt-4">
                    <base-button
                        v-if="!hideDelete"
                        type="danger"
                        @click="deletePerson">
                        {{ deleteLabel }}
                    </base-button>
                    <div />
                    <ValidationButton
                        button-label="Speichern"
                        :errors="errors"
                        :invalid="!valid"
                        @click="deactivateEditMode"
                        @validate="validate()" />
                </div>
            </div>
            <StoreValidationTransmitter
                :valid="valid"
                :step-name="stepName"
                section="person-card" />
        </NestedForm>
    </PersonCardWrapper>
</template>

<script>
import PersonCardWrapper from "@/components/pages/registration/persons/PersonCardWrapper";
import registrationPersonMixin from "@/mixins/registration/registrationPersonMixin";
import formSelectChoiceMixin from "@/mixins/registration/formSelectChoiceMixin";
import isEmpty from 'lodash/isEmpty'
import OrfEval from "@/components/pages/registration/OrfEval";
import RegistrationAddressFormMain from "@/components/pages/registration/address/RegistrationAddressFormMain";
import ParticipantSalutation from "@/components/pages/registration/persons/ParticipantSalutation";
import ParticipantGender from "@/components/pages/registration/persons/ParticipantGender";
import InvoiceRecipientSalutation from "@/components/pages/registration/persons/InvoiceRecipientSalutation";
import InvoiceRecipientGender from "@/components/pages/registration/persons/InvoiceRecipientGender";
import registrationCaregiverMixin from "@/mixins/registration/registrationCaregiverMixin";
import debounce from 'lodash/debounce'
import registrationOnlineRegistrationFieldsMixin from "@/mixins/registration/registrationOnlineRegistrationFieldsMixin";
import PersonName from "@/components/pages/registration/persons/PersonName";
import StoreValidationTransmitter from '../StoreValidationTransmitter.vue'
import {FormContextKey} from 'vee-validate';

export default {
    name: "PersonCard",
    components: {
        StoreValidationTransmitter,
        ParticipantGender,
        PersonName,
        InvoiceRecipientSalutation,
        InvoiceRecipientGender,
        ParticipantSalutation,
        RegistrationAddressFormMain,
        OrfEval,
        PersonCardWrapper
    },
    mixins: [registrationPersonMixin, formSelectChoiceMixin, registrationCaregiverMixin, registrationOnlineRegistrationFieldsMixin],
    inject: {stepName: {from: 'stepName'}, globalValidator: {from: FormContextKey}},
    provide() {
        return {
            [FormContextKey]: this.globalValidator
        }
    },
    props: {
        index: {
            default: null
        },
        registeringPersonType: {
            required: true,
            type: String,
            description: "participant or invoiceRecipient"
        },
        hideDelete: {
            type: Boolean,
            default: false
        },
        deleted: {
            type: Boolean,
            default: false
        },
        mountOpened: {
            type: Boolean,
            default: false
        },
        mountOpenedIfNotValid: {
            type: Boolean,
            default: false
        },
        selectMode: {
            type: Boolean,
            default: false
        },
        viewOnly: {
            type: Boolean,
            default: false
        },
        delayRender: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editMode: false,
            phoneInvalid: false,
            cancelCheckPhone: null,
            testValue: null,
            photosAgreementTextShortened: true,
            checkingPhone: false
        }
    },
    computed: {
        groupRegistrarShouldNotPreloadAllPersons() {
            return this.registeringPersonType === 'invoiceRecipient' || !(this.$store.state.registration.groupRegistrarToken || this.registrationData.persons?.length > 10) || this.editMode
        },
        globalValidatorModel() {
            return this.globalValidator
        },
        shortQueryPhotosAgreementTextToLength() {
            return 600
        },
        shortQueryPhotosAgreementTextMustBeShortened() {
            const text = typeof this.photosAgreementTextForPerson === 'string' ? this.photosAgreementTextForPerson : ''
            return this.shortQueryPhotosAgreementTextToLength < text.length
        },
        queryPhotosAgreementText() {
            let text = typeof this.photosAgreementTextForPerson === 'string' ? this.photosAgreementTextForPerson : ''
            if (this.photosAgreementTextShortened && this.shortQueryPhotosAgreementTextMustBeShortened)
                text = text.substring(0, this.shortQueryPhotosAgreementTextToLength) + '...'
            return text
        },
        photosAgreementTextForPerson() {
            let text = this.eventData.queryPhotosAgreementText ?? ''

            text = text.replaceAll('{{vorname}}', this.person?.firstname ?? '')
            text = text.replaceAll('{{nachname}}', this.person?.lastname ?? '')
            text = text.replaceAll('{{veranstaltung}}', this.eventData?.shortText ?? '')

            return text
        },
        birthdayRules() {
            return {
                correctAge: true,
                correctDateFormat: true,
                ...this.registeringPersonType === 'invoiceRecipient' &&
                    {
                        minAgeInvoice: {
                            minAge: this.eventData.chargeable ? (this.invoiceRecipient1417 ? 14 : 18) : false
                        },
                        minAgeContactPerson: {
                            minAge: !this.eventData.chargeable ? (this.invoiceRecipient1417 ? 14 : 18) : false
                        }
                    },
                ...this.registeringPersonType === 'participant' && !this.noCheckToken && !this.isCaregiver &&
                    {
                        minAgeEventParticipant: {
                            eventStartDate: this.eventData?.startDate,
                            minAge: this.eventData?.ageFrom ? this.eventData.ageFrom : 0,
                            monthsOfAgeTolerance: this.eventData?.ageToleranceMonthsDown
                        },
                        maxAgeEventParticipant: {
                            eventEndDate: this.eventData.endDate,
                            maxAge: this.eventData.ageTo,
                            monthsOfAgeTolerance: this.eventData.ageToleranceMonthsUp
                        }
                    }
            }
        },
        birthdayNotStandardized() {
            if (!this.person.birthday) return false
            const birthdayDate = this.$moment(this.person.birthday)
            return this.person.birthday !== birthdayDate.format('DD.MM.YYYY')
                && this.person.birthday !== birthdayDate.format('YYYY-MM-DD')
        },
        recognizedBirthday() {
            if (!this.person.birthday) return null
            return this.$moment(this.person.birthday).format('DD.MM.YYYY')
        },
        showOwnResourcesAgreement() {
            if (this.person.type === 2) return false
            if (!this.invoiceRecipient1417) return false

            const age = this.$moment().diff(this.$moment(this.person.birthday), 'years')
            if (age < 14) return false
            if (age >= 18) return false

            return true
        },
        noEditBodyRendered() {
            return this.viewOnly || this.selectMode || this.deleted
        },
        deleteLabel() {
            return this.registeringPersonType === 'invoiceRecipient'
                ? this.$t('eventRegistration.person.removeSelection')
                : this.$t('eventRegistration.person.delete')
        }
    },
    methods: {
        activateEditMode() {
            this.editMode = true
        },
        async deactivateEditMode() {
            if (this.$refs.personValidator?.valid) {
                this.editMode = false
            }
        },
        async activateEditModeIfNotValid() {
            this.validating = true
            setTimeout(async () => {
                const result = await this.$refs.personValidator.validate({mode: 'silent', warn: false})
                if (!result) this.editMode = true
                this.validating = false
            }, 50)
        },
        deletePerson() {
            this.$emit('delete')
        },
        restorePerson() {
            this.$emit('restore')
        },
        checkPhone(number) {
            this.cancelCheckPhone && this.cancelCheckPhone();
            this.checkPhoneDebounced(number)
        },
        checkPhoneDebounced: debounce(async function (number) {
            this.checkingPhone = true
            this.phoneInvalid = false
            this.cancelCheckPhone = this.$axios.CancelToken?.source();
            try {
                const result = await this.$axios.post(
                    this.$apiRoute('api.registration.validation'),
                    {
                        type: "phone",
                        data: number
                    },
                    {
                        cancelToken: this.cancelCheckPhone?.token
                    }
                )
                this.phoneInvalid = !result.data.isValid
            } catch (e) {
                console.error(e)
            }
            this.checkingPhone = false
        }, 700)
    },
    mounted() {
        if ((isEmpty(this.personAsObjectWithoutCaregiverToken) || this.mountOpened) && !this.selectMode && !this.viewOnly && !this.deleted) {
            this.activateEditMode()
        } else if (this.mountOpenedIfNotValid) {
            this.$nextTick(() => {
                this.activateEditModeIfNotValid()
            })
        }
    },
    watch: {
        'person.phone': {
            async handler(number) {
                if (number)
                    this.checkPhone(number)
            },
            immediate: true
        },
        'person.type': {
            async handler() {
                const newPerson = this.$cloneDeep(this.person)
                //Clear Data, which is not visible
                if (newPerson.type === 2) {
                    newPerson.firstname = null
                    newPerson.birthday = null
                    newPerson.salutation = null
                    newPerson.acadTitle = null

                }
                if (newPerson.type !== 2 && newPerson.addressType !== 4) {
                    newPerson.toTheAttention = null
                }
                this.person = newPerson
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "sass:color";

.name-edit-header {
    gap: map.get($spacers, 2)
}

@include media-breakpoint-down(sm) {
    .d-md-down-none {
        display: none;
    }
}

.deleted {
    .name-heading {
        text-decoration: line-through;
    }

    :deep(.participant-card) {
        background: color-mix(in srgb, var(--custom-surface-color, $white) 20%, transparent);
    }
}

.photos-agreement-text {
    white-space: pre-wrap;
    margin-top: map.get($spacers, 3);
}

</style>
